

import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  useSurveyDataContext,
} from "../../app/audit/AI/PulseSurvey_V2/Data/SurveyViewContext";
import PulseSurveyHeader from "./PulseSurveyHeader";
import { GET_EMPLOYEES } from '../../constants/actions';

// Icons (inline svgs to avoid new deps)
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
  </svg>
);

const MoreVertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
  </svg>
);

const UserPlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15,14C12.33,14 7,15.33 7,18V20H23V18C23,15.33 17.67,14 15,14M6,10V7H4V10H1V12H4V15H6V12H9V10M15,12A4,4 0 0,0 19,8A4,4 0 0,0 15,4A4,4 0 0,0 11,8A4,4 0 0,0 15,12Z"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

// Styled containers (no outer header; this is meant to live under the scheduler header)
const Container = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  overflow: hidden;
  margin-bottom: 32px;   /* breathing room below the card */
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderLeft = styled.div``;
const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const OptionsMenuWrapper = styled.div`
  position: relative;
`;

const OptionsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover { background: #f8f9fa; border-color: #c0c0c0; }
`;

const OptionsMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  padding: 6px 0;
  min-width: 220px;
  z-index: 10;
`;

const OptionsMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  text-align: left;
  font-family: "Poppins";
  font-size: 13px;
  color: ${props => props.danger ? '#dc3545' : '#414141'};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover { background: ${props => props.danger ? '#fff5f5' : '#f8f9fa'}; }
`;

const TableHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  color: #7e7e7e;
`;

const SearchInput = styled.input`
  padding: 8px 12px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  width: 240px;
  transition: border-color 0.2s;

  &:focus { outline: none; border-color: #476dfa; }
  &::placeholder { color: #aaa; }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  padding-bottom: 24px;   /* space at the bottom so content doesn't crash into the edge */
  min-height: 360px;      /* gives the table a sense of space even when few/no rows */
`;

const RecipientsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #fafafa;
`;

const TableHeadRow = styled.tr``;

const TableHeadCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  transition: background-color 0.2s;

  &:hover { background: #f0f4ff; }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

const RecipientName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  background: ${props => props.status === 'completed' ? '#e8f5e8' : '#fff4e6'};
  color: ${props => props.status === 'completed' ? '#2e7d2e' : '#cc8500'};
`;

const ActionButtonSmall = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid ${props => props.danger ? '#dc3545' : '#e0e0e0'};
  background: ${props => props.danger ? '#fff5f5' : 'white'};
  border-radius: 6px;
  color: ${props => props.danger ? '#dc3545' : '#7e7e7e'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.danger ? '#dc3545' : '#f8f9fa'};
    color: ${props => props.danger ? 'white' : '#414141'};
  }
`;


const AddRecipientsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const AddRecipientsModal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;


const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-family: 'Poppins';
  font-weight: 600;
  font-size: 18px;
  color: #414141;
`;


const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid #f0f0f0;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 7px;
  font-family: 'Poppins';
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
`;

const CancelButton = styled(ModalButton)`
  background: transparent;
  border: 1px solid #e0e0e0;
  color: #7e7e7e;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const ConfirmButton = styled(ModalButton)`
  background: #476dfa;
  border: none;
  color: white;

  &:hover {
    background: #3d5ce6;
  }
`;


const ManagePulse = ({ surveyId, onBreadcrumbChange }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    surveyInfo = {},
    surveyParticipants = [],
    handleCloseSurvey,
    handleArchiveSurvey,
    addEmployeeReportAccess,
    removeEmployeeReportAccess,
    fetchSurvey,
    compliance = {},
    saveChangesAndAssignUsers,
    get_employee_org_category,
    get_survey_structure,
    fetched_scheduled_task,
  } = useSurveyDataContext();


  // Redux state for employees
  const { employees } = useSelector(
    (state) => ({
      employees: Array.isArray(state.employees?.employees) ? state.employees.employees : [],
    }),
    shallowEqual,
  );

  // Local UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isAddRecipientsModalOpen, setIsAddRecipientsModalOpen] = useState(false);
  const [isAddReportAccessModalOpen, setIsAddReportAccessModalOpen] = useState(false);
  
  // Report access management state
  const [reportAccessUsers, setReportAccessUsers] = useState([]);
  const [reportAccessSearchTerm, setReportAccessSearchTerm] = useState("");

  // Employee selection state (from ScheduleSurvey pattern)
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('individual'); // "individual" or "category"
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Load employees if needed
  useEffect(() => {
    if (!employees?.length) {
      dispatch({ type: GET_EMPLOYEES });
    }
  }, [dispatch, employees?.length]);

  useEffect(() => {
    if (typeof onBreadcrumbChange !== "function") {
      return;
    }

    if (surveyInfo?.title) {
      onBreadcrumbChange([{ label: surveyInfo.title }]);
    } else {
      onBreadcrumbChange([]);
    }
  }, [onBreadcrumbChange, surveyInfo]);

  // Extract report access users from fetched survey data
  useEffect(() => {
    if (fetched_scheduled_task && employees?.length > 0) {
      // Based on EmployeeAccess.js, the data is stored in fetched_scheduled_task.employee_access as an array of employee IDs
      const reportAccessIds = fetched_scheduled_task.employee_access || [];
      console.log(fetched_scheduled_task)
      if (Array.isArray(reportAccessIds) && reportAccessIds.length > 0) {
        const usersWithAccess = reportAccessIds.map(employeeId => {
          const employee = employees.find(emp => emp.id === employeeId);
          return {
            id: employeeId,
            employee_full_name: employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown User',
            email: employee?.email || '',
            access_granted_date: null, // This might not be available in the current data structure
          };
        }).filter(user => user.id && user.employee_full_name !== 'Unknown User'); // Filter out invalid entries
        
        setReportAccessUsers(usersWithAccess);
      } else {
        setReportAccessUsers([]);
      }
    }
  }, [fetched_scheduled_task, employees]);

  // Build accordion categories (from ScheduleSurvey pattern)
  const accordionCategories = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    return get_survey_structure[0].categories
      .filter((cat) => cat.visibility !== 'private' && !cat.demographic && cat.priority !== 'primary' && cat.priority !== 'secondary')
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        priority: cat.priority,
        type: 'employee-filtering',
        options: cat.options.map((opt) => ({
          id: opt.id,
          name: opt.name,
          text: opt.text || opt.name,
        })),
      }));
  }, [get_survey_structure]);

  // Filter employees based on search term and selected categories
  const filteredEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];

    let filtered = employees.filter((emp) => emp.status === 1); // Only active employees

    // Filter by categories if any are selected
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((emp) => {
        const empCategories = get_employee_org_category?.find(
          (empCat) => empCat.employee === emp.id,
        );

        if (!empCategories?.category) return false;

        // Employee must match ALL selected categories
        return selectedCategories.every((selectedCatId) => {
          return empCategories.category.some(
            (cat) => cat.value_id === selectedCatId || cat.value === selectedCatId,
          );
        });
      });
    } else if (employeeSearchTerm) {
      // Only apply search filter if no categories are selected
      filtered = filtered.filter(
        (emp) =>
          emp.first_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
          emp.last_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
          emp.email?.toLowerCase().includes(employeeSearchTerm.toLowerCase()),
      );
    }

    return filtered;
  }, [employees, employeeSearchTerm, selectedCategories, get_employee_org_category]);

  // Handle employee selection
  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId],
    );
  };

  // Handle category selection
  const handleCategoryToggle = (categoryOptionId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryOptionId)
        ? prev.filter((id) => id !== categoryOptionId)
        : [...prev, categoryOptionId],
    );
  };

  // Toggle category expand
  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategory((prev) => (prev === categoryId ? null : categoryId));
  };

  // Add employees from category selection
  const addCategoryEmployees = () => {
    const employeeIds = filteredEmployees.map((emp) => emp.id);
    setSelectedEmployees((prev) => [...new Set([...prev, ...employeeIds])]);
    setSelectedCategories([]);
    setExpandedCategory(null);
  };

  // Clear all selected employees
  const clearAllEmployees = () => {
    setSelectedEmployees([]);
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(surveyParticipants)) return [];
    if (!searchTerm) return surveyParticipants;
    const q = searchTerm.toLowerCase();
    return surveyParticipants.filter((p) =>
      (p.employee_full_name || "").toLowerCase().includes(q)
    );
  }, [surveyParticipants, searchTerm]);

  // Derive recipient count even when participants list is hidden for anonymous surveys
  const recipientsCount = useMemo(() => {
    // 1) If participants are present, use them
    if (Array.isArray(surveyParticipants) && surveyParticipants.length > 0) {
      return surveyParticipants.length;
    }

    // 2) Try common shapes on `compliance`
    try {
      if (Array.isArray(compliance?.participants)) return compliance.participants.length;
      const completedLen = Array.isArray(compliance?.completed) ? compliance.completed.length : 0;
      const pendingLen = Array.isArray(compliance?.pending) ? compliance.pending.length : 0;
      const employeesLen = Array.isArray(compliance?.employees) ? compliance.employees.length : 0;
      const totals = [
        Number.isFinite(compliance?.total) ? compliance.total : 0,
        Number.isFinite(compliance?.total_requests) ? compliance.total_requests : 0,
        completedLen + pendingLen,
        employeesLen,
      ].filter((n) => n && Number.isFinite(n));
      if (totals.length) return Math.max(...totals);
    } catch (_) {}

    // 3) Fall back to fields on fetched_scheduled_task if available
    try {
      const reqLen = Array.isArray(fetched_scheduled_task?.request_list)
        ? fetched_scheduled_task.request_list.length
        : 0;
      const compLen = Array.isArray(fetched_scheduled_task?.compliance)
        ? fetched_scheduled_task.compliance.length
        : 0;
      const totals = [
        reqLen,
        compLen,
        Number.isFinite(fetched_scheduled_task?.total_requests)
          ? fetched_scheduled_task.total_requests
          : 0,
      ].filter((n) => n && Number.isFinite(n));
      if (totals.length) return Math.max(...totals);
    } catch (_) {}

    return 0;
  }, [surveyParticipants, compliance, fetched_scheduled_task]);

  const removeAssignee = (participantId) => {
    // Use the same removal function used on Access tab
    if (typeof removeEmployeeReportAccess === 'function') {
      removeEmployeeReportAccess(participantId);
    }
  };

  const fetchSurveyRef = React.useRef(fetchSurvey);
  fetchSurveyRef.current = fetchSurvey;

  React.useEffect(() => {
    if (surveyId && typeof fetchSurveyRef.current === 'function') {
      fetchSurveyRef.current(surveyId);
    }
  }, [surveyId]);

  const handleCloseFromHeader = () => setIsCloseModalOpen(true);
  const handleArchiveFromHeader = () => setIsArchiveModalOpen(true);

  const handleAddRecipients = () => {
    if (selectedEmployees.length > 0) {
      if (typeof saveChangesAndAssignUsers === 'function') {
        saveChangesAndAssignUsers(surveyInfo?.task_template_id || surveyId, selectedEmployees);
      }
      clearAllEmployees();
      setIsAddRecipientsModalOpen(false);
    }
  };

  // Report access management functions
  const handleAddReportAccess = () => {
    if (selectedEmployees.length > 0 && typeof addEmployeeReportAccess === 'function') {
      addEmployeeReportAccess(surveyId, selectedEmployees);
      clearAllEmployees();
      setIsAddReportAccessModalOpen(false);
      
      // Refetch survey data to get updated report access list
      setTimeout(() => {
        if (typeof fetchSurvey === 'function') {
          fetchSurvey(surveyId);
        }
      }, 1000); // Small delay to ensure the backend has processed the request
    }
  };

  const handleRemoveReportAccess = (userId) => {
    if (typeof removeEmployeeReportAccess === 'function') {
      removeEmployeeReportAccess(surveyInfo?.task_template_id || surveyId, [userId]);
      
      // Optimistically remove from local state and also refetch to be sure
      setReportAccessUsers(prev => prev.filter(user => user.id !== userId));
      
      // Also refetch survey data to ensure consistency
      setTimeout(() => {
        if (typeof fetchSurvey === 'function') {
          fetchSurvey(surveyId);
        }
      }, 1000);
    }
  };

  // Reset modal state when opening
  const openAddRecipientsModal = () => {
    setSelectedEmployees([]);
    setEmployeeSearchTerm('');
    setSelectedCategories([]);
    setActiveTab('individual');
    setExpandedCategory(null);
    setIsAddRecipientsModalOpen(true);
  };

  return (
    <>
      <PulseSurveyHeader
        title={surveyInfo.title || "Pulse Survey"}
        description={surveyInfo.description || ""}
        createdBy={surveyInfo.createdBy || ""}
        createdDate={surveyInfo.createdDate || ""}
        updatedDate={surveyInfo.updatedDate || ""}
        surveyIsAnonymous={surveyInfo.surveyIsAnonymous || false}
        surveyIsOpen={surveyInfo.surveyIsOpen || false}
        task_template_id={surveyInfo.task_template_id || 0}
        compliance={compliance}
        // We intentionally do not pass onSelectClick to hide/disable Select in Manage view
        // We intentionally do not pass onChatClick to hide Chat with Data in Manage view
        onCloseSurvey={handleCloseFromHeader}
        onArchiveSurvey={handleArchiveFromHeader}
        backAction={() => navigate('/leader/audit/schedule-v2')}
      />

      <Container>
      <TableHeader>
        <HeaderLeft>
          <Title>
            Survey Participants ({surveyInfo?.surveyIsAnonymous ? recipientsCount : filtered.length})
          </Title>
        </HeaderLeft>
        <HeaderRight>
          <TableHeaderActions>
            <SearchContainer>
              <SearchIconWrapper><SearchIcon /></SearchIconWrapper>
              <SearchInput
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search participants..."
              />
            </SearchContainer>
            <AddRecipientsButton onClick={openAddRecipientsModal}>
              <UserPlusIcon />
              <span>Add Recipients</span>
            </AddRecipientsButton>
          </TableHeaderActions>

          <OptionsMenuWrapper>
            <OptionsButton onClick={() => setShowOptionsMenu(v => !v)}>
              Manage
              <MoreVertIcon />
            </OptionsButton>
            {showOptionsMenu && (
              <OptionsMenu>
                {surveyInfo?.surveyIsOpen ? (
                  <OptionsMenuItem danger onClick={() => { setShowOptionsMenu(false); setIsCloseModalOpen(true); }}>
                    Close Survey
                  </OptionsMenuItem>
                ) : (
                  <OptionsMenuItem onClick={() => { setShowOptionsMenu(false); setIsArchiveModalOpen(true); }}>
                    Archive Survey
                  </OptionsMenuItem>
                )}
              </OptionsMenu>
            )}
          </OptionsMenuWrapper>
        </HeaderRight>
      </TableHeader>

      <TableContainer>
        <RecipientsTable>
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell>Actions</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {surveyInfo?.surveyIsAnonymous ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <EmptyState>
                    <EmptyTitle>Survey is anonymous</EmptyTitle>
                    <EmptyText>
                      Users will not be listed here.
                    </EmptyText>
                  </EmptyState>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filtered.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <RecipientName>{p.employee_full_name}</RecipientName>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={(p.status || 'pending').toLowerCase()}>
                        {p.status || 'Pending'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <ActionButtonSmall danger onClick={() => removeAssignee(p.id)} title="Remove">
                        <DeleteIcon />
                      </ActionButtonSmall>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <EmptyState>No participants found</EmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </RecipientsTable>
      </TableContainer>
    </Container>

    {/* Report Access Section */}
    <Container>
      <TableHeader>
        <HeaderLeft>
          <Title>Report Access ({reportAccessUsers.filter(user => 
            !reportAccessSearchTerm || 
            user.employee_full_name.toLowerCase().includes(reportAccessSearchTerm.toLowerCase())
          ).length})</Title>
        </HeaderLeft>
        <HeaderRight>
          <TableHeaderActions>
            <SearchContainer>
              <SearchIconWrapper><SearchIcon /></SearchIconWrapper>
              <SearchInput
                type="text"
                value={reportAccessSearchTerm}
                onChange={(e) => setReportAccessSearchTerm(e.target.value)}
                placeholder="Search users with access..."
              />
            </SearchContainer>
            <AddRecipientsButton onClick={() => {
              setSelectedEmployees([]);
              setEmployeeSearchTerm('');
              setSelectedCategories([]);
              setActiveTab('individual');
              setExpandedCategory(null);
              setIsAddReportAccessModalOpen(true);
            }}>
              <UserPlusIcon />
              <span>Grant Access</span>
            </AddRecipientsButton>
          </TableHeaderActions>
        </HeaderRight>
      </TableHeader>

      <TableContainer>
        <RecipientsTable>
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>User</TableHeadCell>
              <TableHeadCell>Email</TableHeadCell>
              <TableHeadCell>Access Granted</TableHeadCell>
              <TableHeadCell>Actions</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {reportAccessUsers
              .filter(user => 
                !reportAccessSearchTerm || 
                user.employee_full_name.toLowerCase().includes(reportAccessSearchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(reportAccessSearchTerm.toLowerCase())
              )
              .map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <RecipientName>{user.employee_full_name}</RecipientName>
                </TableCell>
                <TableCell>
                  <div style={{ fontFamily: 'Poppins', fontSize: '13px', color: '#7e7e7e' }}>
                    {user.email}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ fontFamily: 'Poppins', fontSize: '12px', color: '#7e7e7e' }}>
                    {user.access_granted_date ? new Date(user.access_granted_date).toLocaleDateString() : 'Unknown'}
                  </div>
                </TableCell>
                <TableCell>
                  <ActionButtonSmall danger onClick={() => handleRemoveReportAccess(user.id)} title="Remove Access">
                    <DeleteIcon />
                  </ActionButtonSmall>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </RecipientsTable>

        {reportAccessUsers.filter(user => 
          !reportAccessSearchTerm || 
          user.employee_full_name.toLowerCase().includes(reportAccessSearchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(reportAccessSearchTerm.toLowerCase())
        ).length === 0 && (
          <EmptyState>
            <EmptyTitle>No users have report access</EmptyTitle>
            <EmptyText>
              {reportAccessSearchTerm ? 'No users match your search criteria' : 'Grant access to users so they can view survey results and reports'}
            </EmptyText>
          </EmptyState>
        )}
      </TableContainer>
    </Container>

      {/* Add Recipients Modal */}
      {isAddRecipientsModalOpen && (
        <AddRecipientsModal onClick={() => setIsAddRecipientsModalOpen(false)}>
          <EnhancedModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Add Recipients</ModalTitle>
            </ModalHeader>
            <EnhancedModalBody>
              {/* Employee Selection Section */}
              <PanelSection>
                <SectionTitle>
                  <UsersIcon />
                  <span>Select Recipients ({selectedEmployees.length} selected)</span>
                </SectionTitle>

                {/* Tab Navigation */}
                <TabContainer>
                  <Tab
                    type="button"
                    active={activeTab === 'individual'}
                    onClick={() => {
                      setActiveTab('individual');
                      setEmployeeSearchTerm('');
                      setSelectedCategories([]);
                    }}
                  >
                    Individual Selection
                  </Tab>
                  <Tab
                    type="button"
                    active={activeTab === 'category'}
                    onClick={() => {
                      setActiveTab('category');
                      setEmployeeSearchTerm('');
                    }}
                  >
                    By Category
                  </Tab>
                </TabContainer>

                <EmployeeSelectionLayout>
                  {/* Left: Selection Interface */}
                  <SelectionPanel>
                    {/* Individual Selection Tab */}
                    {activeTab === 'individual' && (
                      <SelectionContent>
                        <SearchContainer>
                          <SearchInputWrapper>
                            <SearchIconWrapper>
                              <SearchIcon />
                            </SearchIconWrapper>
                            <EmployeeSearchInput
                              type="text"
                              value={employeeSearchTerm}
                              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                              placeholder="Search employees..."
                            />
                          </SearchInputWrapper>
                          <SearchHint>
                            {employeeSearchTerm
                              ? `${filteredEmployees.length} employee${
                                  filteredEmployees.length !== 1 ? 's' : ''
                                } found`
                              : `${employees.length} total employees available`}
                          </SearchHint>
                        </SearchContainer>

                        {employeeSearchTerm ? (
                          <EmployeesList>
                            {filteredEmployees.map((emp) => (
                              <EmployeeItem
                                key={emp.id}
                                selected={selectedEmployees.includes(emp.id)}
                                onClick={() => handleEmployeeToggle(emp.id)}
                              >
                                <EmployeeCheckbox
                                  type="checkbox"
                                  checked={selectedEmployees.includes(emp.id)}
                                  onChange={(e) => { e.stopPropagation(); handleEmployeeToggle(emp.id); }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <EmployeeDetails>
                                  <EmployeeName>
                                    {emp.first_name} {emp.last_name}
                                  </EmployeeName>
                                  <EmployeeEmail>{emp.email}</EmployeeEmail>
                                </EmployeeDetails>
                              </EmployeeItem>
                            ))}
                            {filteredEmployees.length === 0 && (
                              <EmptyState>
                                <EmptyTitle>No employees found</EmptyTitle>
                                <EmptyText>Try adjusting your search terms</EmptyText>
                              </EmptyState>
                            )}
                          </EmployeesList>
                        ) : (
                          <SearchPrompt>
                            <SearchPromptIcon>👥</SearchPromptIcon>
                            <SearchPromptTitle>Search for employees</SearchPromptTitle>
                            <SearchPromptText>
                              Start typing to find employees by name or email
                            </SearchPromptText>
                          </SearchPrompt>
                        )}
                      </SelectionContent>
                    )}

                    {/* Category Selection Tab */}
                    {activeTab === 'category' && (
                      <SelectionContent>
                        {(selectedCategories.length > 0) && (
                          <CategoryAddSection>
                            <CategoryAddInfo>
                              {filteredEmployees.length} employee
                              {filteredEmployees.length !== 1 ? 's' : ''} match your selection
                            </CategoryAddInfo>
                            <CategoryAddButton type="button" onClick={addCategoryEmployees}>
                              Add {filteredEmployees.length} Employee
                              {filteredEmployees.length !== 1 ? 's' : ''}
                            </CategoryAddButton>
                          </CategoryAddSection>
                        )}

                        {accordionCategories.length > 0 ? (
                          <CategoryFilters>
                            {accordionCategories.map((category) => {
                              const selectedCount = category.options.filter((opt) =>
                                selectedCategories.includes(opt.id)
                              ).length;

                              return (
                                <CategoryGroup key={category.id}>
                                  <CategoryGroupHeader
                                    onClick={() => toggleCategoryExpand(category.id)}
                                  >
                                    <CategoryGroupLeft>
                                      <CategoryCollapseIcon
                                        expanded={expandedCategory === category.id}
                                      >
                                        {expandedCategory === category.id ? '▼' : '▶'}
                                      </CategoryCollapseIcon>
                                      <CategoryName>{category.name}</CategoryName>
                                    </CategoryGroupLeft>
                                    <CategoryCount>
                                      {selectedCount} of {category.options.length}
                                    </CategoryCount>
                                  </CategoryGroupHeader>

                                  {expandedCategory === category.id && (
                                    <CategoryOptions>
                                      {category.options.map((option) => {
                                        const isSelected = selectedCategories.includes(option.id);
                                        
                                        return (
                                          <CategoryOption
                                            key={option.id}
                                            selected={isSelected}
                                            onClick={() => handleCategoryToggle(option.id)}
                                          >
                                            <CategoryOptionCheckbox
                                              type="checkbox"
                                              checked={isSelected}
                                              readOnly
                                            />
                                            <CategoryOptionText>{option.name}</CategoryOptionText>
                                          </CategoryOption>
                                        );
                                      })}
                                    </CategoryOptions>
                                  )}
                                </CategoryGroup>
                              );
                            })}
                          </CategoryFilters>
                        ) : (
                          <EmptyState>
                            <EmptyTitle>No categories available</EmptyTitle>
                            <EmptyText>
                              Contact your administrator to set up employee categories
                            </EmptyText>
                          </EmptyState>
                        )}
                      </SelectionContent>
                    )}
                  </SelectionPanel>

                  {/* Right: Selected Employees */}
                  <SelectedPanel>
                    <SelectedPanelHeader>
                      <SummaryTitle>
                        Selected Recipients ({selectedEmployees.length})
                      </SummaryTitle>
                      {selectedEmployees.length > 0 && (
                        <ClearButton onClick={clearAllEmployees}>Clear All</ClearButton>
                      )}
                    </SelectedPanelHeader>

                    {selectedEmployees.length > 0 ? (
                      <SelectedEmployeesFullList>
                        {selectedEmployees.map((empId) => {
                          const emp = employees.find((e) => e.id === empId);
                          return emp ? (
                            <SelectedEmployeeFullCard key={empId}>
                              <SelectedEmployeeDetails>
                                <SelectedEmployeeName>
                                  {emp.first_name} {emp.last_name}
                                </SelectedEmployeeName>
                                <SelectedEmployeeEmail>{emp.email}</SelectedEmployeeEmail>
                                {emp.role_name && (
                                  <SelectedEmployeeRole>{emp.role_name}</SelectedEmployeeRole>
                                )}
                              </SelectedEmployeeDetails>
                              <RemoveButton onClick={() => handleEmployeeToggle(empId)}>
                                ×
                              </RemoveButton>
                            </SelectedEmployeeFullCard>
                          ) : null;
                        })}
                      </SelectedEmployeesFullList>
                    ) : (
                      <EmptySelectedState>
                        <EmptySelectedIcon>👥</EmptySelectedIcon>
                        <EmptySelectedTitle>No recipients selected</EmptySelectedTitle>
                        <EmptySelectedText>
                          Use the selection options on the left to choose survey recipients.
                        </EmptySelectedText>
                      </EmptySelectedState>
                    )}
                  </SelectedPanel>
                </EmployeeSelectionLayout>
              </PanelSection>
            </EnhancedModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setIsAddRecipientsModalOpen(false)}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={handleAddRecipients} disabled={selectedEmployees.length === 0}>
                Add {selectedEmployees.length} Recipients
              </ConfirmButton>
            </ModalFooter>
          </EnhancedModalContent>
        </AddRecipientsModal>
      )}

      {/* Modals (reuse DeleteModal semantics from PulseDataViewPage) */}
      {isCloseModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={() => setIsCloseModalOpen(false)}>
          <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 480, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: 24, borderBottom: '1px solid #f0f0f0' }}>
              <h3 style={{ margin: 0, fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, color: '#414141' }}>Close Survey</h3>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ margin: 0, fontFamily: 'Poppins', fontSize: 14, color: '#414141' }}>
                Are you sure you want to <strong>close</strong> this survey? <br /> This action cannot be undone.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px 24px', borderTop: '1px solid #f0f0f0' }}>
              <button onClick={() => setIsCloseModalOpen(false)} style={{ padding: '10px 20px', background: 'transparent', border: '1px solid #e0e0e0', borderRadius: 7, color: '#7e7e7e', cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { setIsCloseModalOpen(false); if (typeof handleCloseSurvey === 'function') handleCloseSurvey(surveyId); }} style={{ padding: '10px 20px', background: '#dc3545', border: 'none', borderRadius: 7, color: 'white', cursor: 'pointer' }}>Close Survey</button>
            </div>
          </div>
        </div>
      )}

      {isArchiveModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={() => setIsArchiveModalOpen(false)}>
          <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 480, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: 24, borderBottom: '1px solid #f0f0f0' }}>
              <h3 style={{ margin: 0, fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, color: '#414141' }}>Archive Survey</h3>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ margin: 0, fontFamily: 'Poppins', fontSize: 14, color: '#414141' }}>
                Are you sure you want to <strong>archive</strong> this survey? <br /> This action cannot be undone.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px 24px', borderTop: '1px solid #f0f0f0' }}>
              <button onClick={() => setIsArchiveModalOpen(false)} style={{ padding: '10px 20px', background: 'transparent', border: '1px solid #e0e0e0', borderRadius: 7, color: '#7e7e7e', cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { setIsArchiveModalOpen(false); if (typeof handleArchiveSurvey === 'function') handleArchiveSurvey(surveyId); }} style={{ padding: '10px 20px', background: '#476dfa', border: 'none', borderRadius: 7, color: 'white', cursor: 'pointer' }}>Archive Survey</button>
            </div>
          </div>
        </div>
      )}

      {/* Report Access Modal */}
      {isAddReportAccessModalOpen && (
        <AddRecipientsModal onClick={() => setIsAddReportAccessModalOpen(false)}>
          <EnhancedModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Grant Report Access</ModalTitle>
            </ModalHeader>
            <EnhancedModalBody>
              {/* Employee Selection Section */}
              <PanelSection>
                <SectionTitle>
                  <UsersIcon />
                  <span>Select Users to Grant Access ({selectedEmployees.length} selected)</span>
                </SectionTitle>

                {/* Tab Navigation */}
                <TabContainer>
                  <Tab
                    type="button"
                    active={activeTab === 'individual'}
                    onClick={() => {
                      setActiveTab('individual');
                      setEmployeeSearchTerm('');
                      setSelectedCategories([]);
                    }}
                  >
                    Individual Selection
                  </Tab>
                  <Tab
                    type="button"
                    active={activeTab === 'category'}
                    onClick={() => {
                      setActiveTab('category');
                      setEmployeeSearchTerm('');
                    }}
                  >
                    By Category
                  </Tab>
                </TabContainer>

                <EmployeeSelectionLayout>
                  {/* Left: Selection Interface */}
                  <SelectionPanel>
                    {/* Individual Selection Tab */}
                    {activeTab === 'individual' && (
                      <SelectionContent>
                        <SearchContainer>
                          <SearchInputWrapper>
                            <SearchIconWrapper>
                              <SearchIcon />
                            </SearchIconWrapper>
                            <EmployeeSearchInput
                              type="text"
                              value={employeeSearchTerm}
                              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                              placeholder="Search employees..."
                            />
                          </SearchInputWrapper>
                          <SearchHint>
                            {employeeSearchTerm
                              ? `${filteredEmployees.length} employee${
                                  filteredEmployees.length !== 1 ? 's' : ''
                                } found`
                              : `${employees.length} total employees available`}
                          </SearchHint>
                        </SearchContainer>

                        {employeeSearchTerm ? (
                          <EmployeesList>
                            {filteredEmployees
                              .filter(emp => !reportAccessUsers.some(user => user.id === emp.id)) // Exclude users who already have access
                              .map((emp) => (
                              <EmployeeItem
                                key={emp.id}
                                selected={selectedEmployees.includes(emp.id)}
                                onClick={() => handleEmployeeToggle(emp.id)}
                              >
                                <EmployeeCheckbox
                                  type="checkbox"
                                  checked={selectedEmployees.includes(emp.id)}
                                  onChange={(e) => { e.stopPropagation(); handleEmployeeToggle(emp.id); }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <EmployeeDetails>
                                  <EmployeeName>
                                    {emp.first_name} {emp.last_name}
                                  </EmployeeName>
                                  <EmployeeEmail>{emp.email}</EmployeeEmail>
                                </EmployeeDetails>
                              </EmployeeItem>
                            ))}
                            {filteredEmployees
                              .filter(emp => !reportAccessUsers.some(user => user.id === emp.id))
                              .length === 0 && (
                              <EmptyState>
                                <EmptyTitle>No employees found</EmptyTitle>
                                <EmptyText>All matching employees already have report access</EmptyText>
                              </EmptyState>
                            )}
                          </EmployeesList>
                        ) : (
                          <SearchPrompt>
                            <SearchPromptIcon>📊</SearchPromptIcon>
                            <SearchPromptTitle>Search for employees</SearchPromptTitle>
                            <SearchPromptText>
                              Start typing to find employees to grant report access
                            </SearchPromptText>
                          </SearchPrompt>
                        )}
                      </SelectionContent>
                    )}

                    {/* Category Selection Tab - Similar to the recipients modal */}
                    {activeTab === 'category' && (
                      <SelectionContent>
                        {(selectedCategories.length > 0) && (
                          <CategoryAddSection>
                            <CategoryAddInfo>
                              {filteredEmployees.filter(emp => !reportAccessUsers.some(user => user.id === emp.id)).length} employee
                              {filteredEmployees.filter(emp => !reportAccessUsers.some(user => user.id === emp.id)).length !== 1 ? 's' : ''} match your selection
                            </CategoryAddInfo>
                            <CategoryAddButton type="button" onClick={addCategoryEmployees}>
                              Add {filteredEmployees.filter(emp => !reportAccessUsers.some(user => user.id === emp.id)).length} Employee
                              {filteredEmployees.filter(emp => !reportAccessUsers.some(user => user.id === emp.id)).length !== 1 ? 's' : ''}
                            </CategoryAddButton>
                          </CategoryAddSection>
                        )}

                        {accordionCategories.length > 0 ? (
                          <CategoryFilters>
                            {accordionCategories.map((category) => {
                              const selectedCount = category.options.filter((opt) =>
                                selectedCategories.includes(opt.id)
                              ).length;

                              return (
                                <CategoryGroup key={category.id}>
                                  <CategoryGroupHeader
                                    onClick={() => toggleCategoryExpand(category.id)}
                                  >
                                    <CategoryGroupLeft>
                                      <CategoryCollapseIcon
                                        expanded={expandedCategory === category.id}
                                      >
                                        {expandedCategory === category.id ? '▼' : '▶'}
                                      </CategoryCollapseIcon>
                                      <CategoryName>{category.name}</CategoryName>
                                    </CategoryGroupLeft>
                                    <CategoryCount>
                                      {selectedCount} of {category.options.length}
                                    </CategoryCount>
                                  </CategoryGroupHeader>

                                  {expandedCategory === category.id && (
                                    <CategoryOptions>
                                      {category.options.map((option) => {
                                        const isSelected = selectedCategories.includes(option.id);
                                        
                                        return (
                                          <CategoryOption
                                            key={option.id}
                                            selected={isSelected}
                                            onClick={() => handleCategoryToggle(option.id)}
                                          >
                                            <CategoryOptionCheckbox
                                              type="checkbox"
                                              checked={isSelected}
                                              readOnly
                                            />
                                            <CategoryOptionText>{option.name}</CategoryOptionText>
                                          </CategoryOption>
                                        );
                                      })}
                                    </CategoryOptions>
                                  )}
                                </CategoryGroup>
                              );
                            })}
                          </CategoryFilters>
                        ) : (
                          <EmptyState>
                            <EmptyTitle>No categories available</EmptyTitle>
                            <EmptyText>
                              Contact your administrator to set up employee categories
                            </EmptyText>
                          </EmptyState>
                        )}
                      </SelectionContent>
                    )}
                  </SelectionPanel>

                  {/* Right: Selected Employees */}
                  <SelectedPanel>
                    <SelectedPanelHeader>
                      <SummaryTitle>
                        Selected Users ({selectedEmployees.length})
                      </SummaryTitle>
                      {selectedEmployees.length > 0 && (
                        <ClearButton onClick={clearAllEmployees}>Clear All</ClearButton>
                      )}
                    </SelectedPanelHeader>

                    {selectedEmployees.length > 0 ? (
                      <SelectedEmployeesFullList>
                        {selectedEmployees.map((empId) => {
                          const emp = employees.find((e) => e.id === empId);
                          return emp ? (
                            <SelectedEmployeeFullCard key={empId}>
                              <SelectedEmployeeDetails>
                                <SelectedEmployeeName>
                                  {emp.first_name} {emp.last_name}
                                </SelectedEmployeeName>
                                <SelectedEmployeeEmail>{emp.email}</SelectedEmployeeEmail>
                                {emp.role_name && (
                                  <SelectedEmployeeRole>{emp.role_name}</SelectedEmployeeRole>
                                )}
                              </SelectedEmployeeDetails>
                              <RemoveButton onClick={() => handleEmployeeToggle(empId)}>
                                ×
                              </RemoveButton>
                            </SelectedEmployeeFullCard>
                          ) : null;
                        })}
                      </SelectedEmployeesFullList>
                    ) : (
                      <EmptySelectedState>
                        <EmptySelectedIcon>📊</EmptySelectedIcon>
                        <EmptySelectedTitle>No users selected</EmptySelectedTitle>
                        <EmptySelectedText>
                          Use the selection options on the left to choose users to grant report access.
                        </EmptySelectedText>
                      </EmptySelectedState>
                    )}
                  </SelectedPanel>
                </EmployeeSelectionLayout>
              </PanelSection>
            </EnhancedModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setIsAddReportAccessModalOpen(false)}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={handleAddReportAccess} disabled={selectedEmployees.length === 0}>
                Grant Access to {selectedEmployees.length} User{selectedEmployees.length !== 1 ? 's' : ''}
              </ConfirmButton>
            </ModalFooter>
          </EnhancedModalContent>
        </AddRecipientsModal>
      )}
  </>
  );
};


export default ManagePulse;

// Additional styled components for ScheduleSurvey-style interface
const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.2);
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const EnhancedModalContent = styled(ModalContent)`
  max-width: 1000px;
  width: 95vw;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EnhancedModalBody = styled(ModalBody)`
  padding: 0;
  flex: 1;
  overflow-y: auto;
`;

const PanelSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  background: #f0f0f0;
  padding: 3px;
  border-radius: 6px;
  margin-bottom: 16px;
  width: fit-content;
`;

const Tab = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-radius: 4px;
  color: ${props => props.active ? '#414141' : '#7e7e7e'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? 'white' : '#e9ecef'};
  }
`;

const EmployeeSelectionLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 16px;
  min-height: 400px;
`;

const SelectionPanel = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const SelectionContent = styled.div`
  padding: 16px;
  min-height: 300px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 6px;
`;

const EmployeeSearchInput = styled.input`
  width: 100%;
  padding: 8px 8px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 12px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const SearchHint = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  padding-left: 12px;
`;

const EmployeesList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;

const EmployeeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f8f8f8;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.selected && `
    background: #f0f4ff;
    border-color: #476dfa;
  `}
`;

const EmployeeCheckbox = styled.input`
  width: 14px;
  height: 14px;
`;

const EmployeeDetails = styled.div`
  flex: 1;
`;

const EmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
  margin-bottom: 2px;
`;

const EmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const EmptyTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const EmptyText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

const SearchPrompt = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SearchPromptIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const SearchPromptTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const SearchPromptText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const CategoryAddSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #e8f5e8;
  border: 1px solid #c8e6c8;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const CategoryAddInfo = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #2e7d2e;
`;

const CategoryAddButton = styled.button`
  padding: 6px 12px;
  background: #2e7d2e;
  border: none;
  border-radius: 4px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #256925;
  }
`;

const CategoryFilters = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
`;

const CategoryGroup = styled.div`
  border-bottom: 1px solid #f8f8f8;

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #fafafa;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

const CategoryGroupLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryCollapseIcon = styled.span`
  font-size: 10px;
  color: #7e7e7e;
  transition: transform 0.2s;
`;

const CategoryName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
`;

const CategoryCount = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const CategoryOptions = styled.div`
  padding: 8px 0;
  background: white;
`;

const CategoryOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 32px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f8f9fa;
  }

  ${props => props.selected && `
    background: #f0f4ff;
  `}
`;

const CategoryOptionCheckbox = styled.input`
  width: 12px;
  height: 12px;
`;

const CategoryOptionText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #414141;
`;

const SelectedPanel = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SelectedPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
`;

const SummaryTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #414141;
`;

const ClearButton = styled.button`
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #ff4444;
    color: #ff4444;
  }
`;

const SelectedEmployeesFullList = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  max-height: 350px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const SelectedEmployeeFullCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    box-shadow: 0 2px 4px rgba(71, 109, 250, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SelectedEmployeeDetails = styled.div`
  flex: 1;
`;

const SelectedEmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 2px;
`;

const SelectedEmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  margin-bottom: 2px;
`;

const SelectedEmployeeRole = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #999;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 4px;
`;

const RemoveButton = styled.button`
  width: 20px;
  height: 20px;
  padding: 0;
  background: #ff4444;
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #cc3333;
  }
`;

const EmptySelectedState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptySelectedIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptySelectedTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const EmptySelectedText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  max-width: 250px;
  line-height: 1.4;
`;
